import { SharedState } from '$utils/sharedState.svelte';
import type { GnosisPayUser } from '$types/gnosisPayUser';
import { stateGnosisPayToken } from './gnosisPayToken.svelte';
import { PAY_USER_ENDPOINT } from '$lib/constants/payApi';
import { clearDelayModuleCache, getDelayModuleAddress } from '$services/gnosisPay';
import type { Address } from 'viem';

interface CacheEntry {
  data: GnosisPayUser;
  expiresAt: number;
}

class StateMeGnosisPay extends SharedState<GnosisPayUser> {
  private cache = new Map<string, CacheEntry>();
  private readonly CACHE_DURATION_MS = 90_000; // 90 seconds
  private loadingPromises = new Map<string, Promise<GnosisPayUser>>();

  load = async (): Promise<void> => {
    const token = stateGnosisPayToken.current;
    if (!token) {
      this.set(null);
      return;
    }

    const cached = this.getCachedData(token);
    if (cached) {
      this.set(cached);
      return;
    }

    // check if already loading for this token to prevent duplicate requests
    const existingPromise = this.loadingPromises.get(token);
    if (existingPromise) {
      const userData = await existingPromise;
      this.set(userData);
      return;
    }

    const loadingPromise = this.fetchUserData(token);
    this.loadingPromises.set(token, loadingPromise);

    try {
      const userData = await loadingPromise;
      this.setCachedData(token, userData);
      this.set(userData);
    } finally {
      this.loadingPromises.delete(token);
    }
  };

  private getCachedData(token: string): GnosisPayUser | null {
    const entry = this.cache.get(token);
    if (!entry) return null;

    if (Date.now() > entry.expiresAt) {
      this.cache.delete(token);
      return null;
    }

    return entry.data;
  }

  private setCachedData(token: string, data: GnosisPayUser): void {
    this.cache.set(token, {
      data,
      expiresAt: Date.now() + this.CACHE_DURATION_MS,
    });
  }

  private async fetchUserData(token: string): Promise<GnosisPayUser> {
    const userResponse = await fetch(PAY_USER_ENDPOINT, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (!userResponse.ok) {
      throw new Error(`Failed to fetch user data: ${userResponse.status}`);
    }

    const gpUser = (await userResponse.json()) as GnosisPayUser;

    if (gpUser.safeWallets[0]?.address) {
      localStorage.setItem('payAddress', gpUser.safeWallets[0].address);
      const delayModuleAddress = await getDelayModuleAddress(
        gpUser.safeWallets[0].address as Address,
      );

      return {
        ...gpUser,
        delayModuleAddress: delayModuleAddress ?? null,
      };
    } else {
      return {
        ...gpUser,
        delayModuleAddress: null,
      };
    }
  }

  invalidateCache = (token?: string): void => {
    if (token) {
      this.cache.delete(token);
    } else {
      const currentToken = stateGnosisPayToken.current;
      if (currentToken) {
        this.cache.delete(currentToken);
      }
    }
    clearDelayModuleCache();
  };

  clearCache = (): void => {
    this.cache.clear();
    this.loadingPromises.clear();
  };
}

export const stateMeGnosisPay = new StateMeGnosisPay(null);
